function line(id, sceneId, characterId, label, directionText, spokenDirectionText, dialogueText, extra = {}) {
  return {
    id, sceneId, type: 'dialogue', visibility: 'character', characterId, label,
    directionText, spokenDirectionText, dialogueText, requireAck: true, ...extra,
  };
}

export const mapRoomMurderScenario = {
  id: 'map-room-murder',
  title: 'Murder in the Map Room',
  category: 'Murder mystery',
  mode: 'mystery',
  duration: '15–20 min',
  logline: 'A young archivist becomes the obvious suspect in a locked-room murder and must trust her own mind long enough to expose the person framing her.',
  description: 'A complete three-act murder mystery with a clear protagonist arc, fair clues, a disaster, a darkest point, and an earned final reveal. The death happens before play and is not shown graphically.',
  characters: [
    {
      id: 'nora', name: 'Nora Finch', role: 'Assistant archivist', color: '#55d7ff', initials: 'NF',
      overview: 'Smart, careful, and observant, but too quick to doubt herself when confident people push back.',
      publicInfo: 'Nora knows the library, its keys, and the map collection better than almost anyone.',
      secret: 'Earlier tonight you warned Dr. Adrian Crowe that Marcus Voss might be falsifying donation records. Crowe told you to stop making accusations until you had proof. You are afraid you were wrong and afraid everyone knows you challenged him.',
      objective: 'Clear your name, protect the archive, and learn to trust what you actually observe instead of waiting for someone more powerful to approve it.',
    },
    {
      id: 'ortiz', name: 'Detective Lena Ortiz', role: 'Police detective', color: '#72e0a6', initials: 'LO',
      overview: 'Calm, disciplined, and unwilling to fall in love with a theory before the evidence earns it.',
      publicInfo: 'Ortiz was called from a nearby event after Dr. Crowe was found dead in the library map room.',
      secret: 'The evidence against Nora is real enough that you cannot ignore it, but her reactions do not fit the simple story everyone is offering you.',
      objective: 'Follow the evidence, protect the innocent, and make Nora prove every claim instead of rescuing her from suspicion.',
    },
    {
      id: 'evelyn', name: 'Evelyn Crowe', role: 'Victim’s niece', color: '#ffb454', initials: 'EC',
      overview: 'Sharp, grieving, and defensive of her family, especially when strangers reduce people to motives.',
      publicInfo: 'Evelyn was helping host the library fundraiser when her uncle was killed.',
      secret: 'You secretly removed a private family letter from the archive earlier tonight because you did not want it displayed. It looks suspicious, but you did not hurt your uncle.',
      objective: 'Protect your family’s privacy, figure out who killed Adrian, and decide when hiding the truth becomes worse than telling it.',
    },
    {
      id: 'voss', name: 'Marcus Voss', role: 'Library board treasurer', color: '#9b8cff', initials: 'MV',
      overview: 'Polished, generous in public, and used to having his version of events accepted first.',
      publicInfo: 'Voss manages major donations and says he met with Dr. Crowe shortly before the murder.',
      secret: 'You killed Dr. Crowe after he confronted you about valuable donated maps you had been quietly selling. During the power flicker, you planted Crowe’s map-room key in Nora’s coat. Later you hid a torn ledger page in her bag. You are counting on Nora doubting herself and on everyone else finding her easier to suspect than you.',
      objective: 'Keep the focus on Nora, protect your alibi, and never volunteer a detail unless someone forces you to.',
    },
  ],
  scenes: [
    {
      id: 'murder-1', number: 1, title: 'The Locked Room', location: 'Hawthorne Library, outside the map room',
      situation: 'Dr. Adrian Crowe has just been found dead inside the locked map room during a stormy fundraiser. Nora found him and called for help.',
      tone: 'Shock, suspicion, and tightening pressure',
    },
    {
      id: 'murder-2', number: 2, title: 'The Evidence Turns', location: 'Hawthorne Library reading room',
      situation: 'The first evidence points toward Nora, but the physical details of the room and the timeline do not fit a simple answer.',
      priorEvent: 'Crowe’s missing key was found in Nora’s coat after Voss reminded everyone she had argued with him earlier.',
      tone: 'Investigation, shifting suspicion, then a hard collapse',
    },
    {
      id: 'murder-3', number: 3, title: 'The Broken Alibi', location: 'The same reading room beside the stopped hall clock',
      situation: 'Nora has been told to step back after a torn ledger page was found in her bag. She now has one chance to explain what everyone else missed.',
      priorEvent: 'Nora realized the map-room door locks automatically when it closes, and a second planted piece of evidence made her look even guiltier.',
      tone: 'Reconstruction, confrontation, and earned triumph',
    },
  ],
  actorBriefs: [
    { id: 'murder-b1-nora', sceneId: 'murder-1', characterId: 'nora', source: 'director', text: 'You are Nora Finch, assistant archivist. You found Dr. Crowe dead in the map room. Earlier tonight you warned him that Marcus Voss might be falsifying donation records, but Crowe told you to stop accusing people without proof. You are scared you were wrong. You are even more scared everyone will decide the argument makes you guilty. Notice details anyway.' },
    { id: 'murder-b1-ortiz', sceneId: 'murder-1', characterId: 'ortiz', source: 'director', text: 'You are Detective Lena Ortiz. Dr. Crowe is dead, the room was found locked, and Nora discovered the body. Do not comfort or accuse too quickly. Build the timeline. Treat Nora as a possible witness and a possible suspect until evidence separates those roles.' },
    { id: 'murder-b1-evelyn', sceneId: 'murder-1', characterId: 'evelyn', source: 'director', text: 'You are Evelyn Crowe, Adrian’s niece. You are grieving and angry. Earlier tonight you secretly took a private family letter from the archive because you did not want it displayed. You did not kill your uncle. Keep the letter secret unless the investigation forces you to admit it.' },
    { id: 'murder-b1-voss', sceneId: 'murder-1', characterId: 'voss', source: 'director', text: 'You are Marcus Voss, library board treasurer. You killed Adrian Crowe after he confronted you about stolen donated maps. During the power flicker you planted Crowe’s key in Nora’s coat. Stay calm. Point out true facts that make Nora look suspicious. Your best weapon is that Nora already doubts herself.' },

    { id: 'murder-b2-nora', sceneId: 'murder-2', characterId: 'nora', source: 'director', text: 'The victim’s key was found in your coat and the detective has not cleared you. You want to help, but every time someone pushes back you feel yourself shrinking. Focus on the library itself. You know how the doors, clocks, records, and archive routines actually work.' },
    { id: 'murder-b2-ortiz', sceneId: 'murder-2', characterId: 'ortiz', source: 'director', text: 'The evidence points at Nora, but you are not satisfied. Make her explain physical facts, not feelings. Watch Voss and Evelyn too. If new evidence appears against Nora, you must respond to it even if you were beginning to believe her.' },
    { id: 'murder-b2-evelyn', sceneId: 'murder-2', characterId: 'evelyn', source: 'director', text: 'You still have the family letter. Nora is under suspicion, and you know what it feels like to hide something that looks worse than it is. When the moment comes, choose whether protecting your secret is worth letting another innocent person stand alone.' },
    { id: 'murder-b2-voss', sceneId: 'murder-2', characterId: 'voss', source: 'director', text: 'Nora has the key and that is helping you. Keep pressing the idea that an archivist had access and opportunity. You also planted a torn ledger page in her bag. When it is found, act surprised. Do not overplay the victory.' },

    { id: 'murder-b3-nora', sceneId: 'murder-3', characterId: 'nora', source: 'director', text: 'The detective told you to step back after a ledger page was found in your bag. This is your lowest point. Then you realized the case only works if you keep trusting everyone else more than yourself. You noticed the stopped clock, the self-locking door, the writing on the back of the page, and who touched your coat. Put the facts together and speak clearly.' },
    { id: 'murder-b3-ortiz', sceneId: 'murder-3', characterId: 'ortiz', source: 'director', text: 'Nora says she can explain the case. Give her one chance, but test each claim. If her reconstruction survives, follow it even if it means abandoning the easy suspect.' },
    { id: 'murder-b3-evelyn', sceneId: 'murder-3', characterId: 'evelyn', source: 'director', text: 'You are ready to stop hiding. You saw Voss move Nora’s coat during the power flicker, and your uncle’s handwriting is on the back of the torn ledger page. Help the truth even if it exposes your own secret too.' },
    { id: 'murder-b3-voss', sceneId: 'murder-3', characterId: 'voss', source: 'director', text: 'Nora is more dangerous now because she has stopped apologizing. Deny, minimize, and attack gaps in her theory. Your alibi depends on a quarter-past clock chime that never actually happened.' },
  ],
  cues: [
    line('murder-c01', 'murder-1', 'nora', 'Nora calls for help',
      'Stand outside the map room and force yourself to say what you found.',
      'Stand still.',
      'Detective... please. Dr. Crowe is in the map room. He is dead.'),

    line('murder-c02', 'murder-1', 'ortiz', 'Secure the room',
      'Block the doorway and take control before anyone enters.',
      'Block the door.',
      'Nobody goes inside. Nora, stay where I can see you. Evelyn, Marcus, both of you stay here too.'),

    line('murder-c03', 'murder-1', 'evelyn', 'Evelyn reels',
      'Look past Ortiz toward the room but do not cross her.',
      'Look toward him.',
      'I saw my uncle twenty minutes ago. He was angry, but he was alive.'),

    line('murder-c04', 'murder-1', 'voss', 'Voss supplies a time',
      'Answer before Ortiz asks you directly. Sound useful, not eager.',
      'Speak calmly.',
      'I met with Adrian just before quarter past eight. I left when the hall clock chimed.'),

    line('murder-c05', 'murder-1', 'ortiz', 'Start the timeline',
      'Turn to Nora and keep your questions simple.',
      'Face Nora.',
      'You found him. Tell me exactly what happened before you opened that door.'),

    line('murder-c06', 'murder-1', 'nora', 'Nora admits the argument',
      'Tell the truth even though you know how bad it sounds.',
      'Tell the truth.',
      'I came to speak with him. We argued earlier, so I waited until the fundraiser thinned out. The door was locked when I got here.'),

    line('murder-c07', 'murder-1', 'ortiz', 'How Nora entered',
      'Look at the keyway, then back at Nora.',
      'Check the lock.',
      'If it was locked, how did you get inside?'),

    line('murder-c08', 'murder-1', 'nora', 'The spare key',
      'Point toward the front desk where library keys are normally signed out.',
      'Point to the desk.',
      'I used the archive spare from the front desk. I opened the door, saw him on the floor, and backed out without touching anything.'),

    line('murder-c09', 'murder-1', 'evelyn', 'Crowe carried his key',
      'Remember your uncle checking his pocket earlier.',
      'Remember it.',
      'Adrian had his own map-room key tonight. He checked for it before the donors arrived.'),

    line('murder-c10', 'murder-1', 'ortiz', 'The victim key is missing',
      'Look toward the room again and state only what you know.',
      'Look inside.',
      'There is no key beside him, on the table, or in the visible part of the room.'),

    line('murder-c11', 'murder-1', 'voss', 'Voss points at access',
      'Glance at Nora’s coat pocket without staring at it.',
      'Glance at Nora.',
      'Nora handles the keys every day. If Adrian’s key is missing, access is not exactly a mystery.'),

    line('murder-c12', 'murder-1', 'nora', 'Nora pushes back',
      'Defend yourself, but let uncertainty creep in when everyone looks at you.',
      'Defend yourself.',
      'I signed out the spare. That is recorded. I did not take Dr. Crowe’s key.'),

    line('murder-c13', 'murder-1', 'ortiz', 'Check Nora’s coat',
      'Hold out your hand for Nora’s coat.',
      'Hold out your hand.',
      'Then we remove guessing. Nora, empty your coat pockets onto the table.'),

    line('murder-c14', 'murder-1', 'nora', 'The key appears',
      'Reach into the pocket and freeze when you feel the metal key.',
      'Check your pocket.',
      'No... this is his key. I swear to you, this was not in my pocket before.'),

    line('murder-c15', 'murder-1', 'voss', 'The disaster lands',
      'Let one reluctant fact fall into the silence.',
      'Look at Ortiz.',
      'There is one more thing. Nora threatened to expose Adrian tonight after their argument.'),

    line('murder-c16', 'murder-1', 'ortiz', 'Nora becomes a suspect',
      'Do not soften the consequence, even if Nora looks terrified.',
      'Stay firm.',
      'Nora, until I understand the key and that argument, you are not just my witness. You are a suspect.'),

    line('murder-c17', 'murder-2', 'nora', 'Nora asks to help',
      'Follow Ortiz into the reading room and try not to sound desperate.',
      'Follow Ortiz.',
      'You can suspect me. Fine. But let me help you understand the building. Half these clues make no sense if you do not know the library.'),

    line('murder-c18', 'murder-2', 'ortiz', 'Ortiz sets the rule',
      'Give Nora one narrow path forward.',
      'Set the rule.',
      'You may explain the building. You do not touch evidence, search bags, or decide who is guilty.'),

    line('murder-c19', 'murder-2', 'evelyn', 'Evelyn confesses a secret',
      'Take the folded family letter from your pocket.',
      'Show the letter.',
      'Then I should say this now. I took a family letter from the archive before dinner. I did not want strangers reading it.'),

    line('murder-c20', 'murder-2', 'ortiz', 'Another secret',
      'Accept the letter without reacting to the embarrassment around it.',
      'Take the letter.',
      'Thank you. Hiding something is not the same as murder, but hiding it until I discover it makes my job harder.'),

    line('murder-c21', 'murder-2', 'nora', 'Nora protects the distinction',
      'Look at Evelyn instead of using her secret to save yourself.',
      'Look at Evelyn.',
      'That letter has nothing to do with Dr. Crowe’s key. We should not turn every bad-looking choice into the same crime.'),

    line('murder-c22', 'murder-2', 'voss', 'Voss presses Nora',
      'Lean back and make your accusation sound reasonable.',
      'Lean back.',
      'Except Nora knew the keys, the room, the records, and Adrian’s schedule. At some point expertise becomes opportunity.'),

    line('murder-c23', 'murder-2', 'nora', 'The door clue',
      'Look at the map-room door closer through the reading-room glass.',
      'Study the door.',
      'Wait. We keep talking like the killer needed Dr. Crowe’s key to get out.'),

    line('murder-c24', 'murder-2', 'ortiz', 'Make Nora prove it',
      'Do not give her the answer. Make her finish the thought.',
      'Make her explain.',
      'Go on.'),

    line('murder-c25', 'murder-2', 'nora', 'The locked room opens',
      'Use the door itself to demonstrate without touching evidence.',
      'Point to the closer.',
      'The map-room door locks automatically when it closes. From inside, anyone can leave. You only need a key to get back in.'),

    line('murder-c26', 'murder-2', 'ortiz', 'The planted-key theory',
      'Look at Crowe’s key on the evidence cloth.',
      'Look at the key.',
      'So the missing key does not explain how the killer escaped. It only explains why someone might want us looking at Nora.'),

    line('murder-c27', 'murder-2', 'nora', 'Nora finds confidence',
      'Let yourself believe the fact you just proved.',
      'Stand taller.',
      'Exactly. That key is not evidence that I entered the room. It is evidence that somebody wanted it found on me.'),

    line('murder-c28', 'murder-2', 'evelyn', 'The coat memory',
      'Look from the key to Marcus and remember the power flicker.',
      'Look at Marcus.',
      'Marcus, you moved Nora’s coat during the blackout. You handed it off the chair because punch spilled beside it.'),

    line('murder-c29', 'murder-2', 'voss', 'Voss explains it away',
      'Answer Evelyn with mild annoyance, not panic.',
      'Stay casual.',
      'I saved her coat from a red punch stain. If kindness is suspicious now, I will adjust.'),

    line('murder-c30', 'murder-2', 'ortiz', 'Lock the timeline',
      'Turn back to Voss and return to his earlier statement.',
      'Face Marcus.',
      'You said you left Crowe when the hall clock chimed quarter past eight. You are certain about that?'),

    line('murder-c31', 'murder-2', 'voss', 'Voss repeats the alibi',
      'Commit to the detail instead of retreating from it.',
      'Commit to it.',
      'Completely. Quarter past. The clock chimed, Adrian told me the meeting was over, and I walked back to the fundraiser.'),

    line('murder-c32', 'murder-2', 'nora', 'Nora notices the stopped clock',
      'Look up at the hall clock and register the frozen hands.',
      'Look at the clock.',
      'The clock says eight oh eight.'),

    line('murder-c33', 'murder-2', 'ortiz', 'Search Nora’s bag',
      'Move to Nora’s bag after another officer signals you.',
      'Check the bag.',
      'Nora, do not touch it. There is a folded sheet inside this side pocket that matches the missing donation ledger.'),

    line('murder-c34', 'murder-2', 'nora', 'The second disaster',
      'See the torn ledger page and lose the confidence you just found.',
      'Look at the page.',
      'That is not mine. I did not put that there either.'),

    line('murder-c35', 'murder-2', 'voss', 'Voss closes the trap',
      'Say very little. Let the evidence do the work.',
      'Stay quiet.',
      'A key in her coat. A missing ledger page in her bag. How many planted objects do we need before the obvious answer is allowed to be obvious?'),

    line('murder-c36', 'murder-2', 'ortiz', 'Nora hits bottom',
      'Stop Nora before she rushes toward the evidence.',
      'Stop Nora.',
      'Step back. Now. You helped me with the door, but this page changes things. Do not say another word until I ask.'),

    line('murder-c37', 'murder-3', 'nora', 'Nora chooses to speak',
      'Stand near the stopped clock and decide you will not disappear into the accusation.',
      'Stand by the clock.',
      'Detective, I know you told me to stop. Give me one minute. If I cannot connect the facts, I will sit down and stay quiet.'),

    line('murder-c38', 'murder-3', 'ortiz', 'One minute',
      'Study Nora, then grant the smallest possible opening.',
      'Give her one minute.',
      'One minute. Facts only.'),

    line('murder-c39', 'murder-3', 'nora', 'Start with the key',
      'Point first to Crowe’s key.',
      'Point to the key.',
      'The key was planted. We proved the killer could leave without it. Whoever put it on me wanted the room to look more locked than it really was.'),

    line('murder-c40', 'murder-3', 'nora', 'Use the ledger page',
      'Point to the folded ledger page without approaching it.',
      'Point to the page.',
      'And that page was folded with the numbers facing out. When Detective Ortiz lifted it, I saw handwriting on the back.'),

    line('murder-c41', 'murder-3', 'evelyn', 'Evelyn identifies the note',
      'Move where you can read the back without touching it.',
      'Read the back.',
      'That is Adrian’s handwriting. “M.V. duplicate sales. Confront at eight. Bring ledger.” M.V. is Marcus Voss.'),

    line('murder-c42', 'murder-3', 'voss', 'Voss attacks the initials',
      'Laugh once, without humor.',
      'Laugh once.',
      'Initials. That is the grand solution? There are hundreds of names those letters could mean.'),

    line('murder-c43', 'murder-3', 'nora', 'Break the alibi',
      'Turn from the page to the stopped hall clock.',
      'Face the clock.',
      'Then let us use your own words. You said the quarter-past chime told you it was time to leave Dr. Crowe.'),

    line('murder-c44', 'murder-3', 'ortiz', 'The clock evidence',
      'Check the clock control panel and read the outage indicator.',
      'Check the panel.',
      'Power dropped at eight oh eight. This clock is electric. It stopped then and never chimed quarter past.'),

    line('murder-c45', 'murder-3', 'voss', 'Voss tries to retreat',
      'Correct yourself too quickly.',
      'Backpedal.',
      'Then I looked at my watch. I misspoke. People misspeak during murder investigations.'),

    line('murder-c46', 'murder-3', 'nora', 'The coat lie',
      'Do not let him change the subject.',
      'Stay on him.',
      'You also said the key proved my access. But Evelyn saw you move my coat during the blackout, when nobody could see what your hands were doing.'),

    line('murder-c47', 'murder-3', 'evelyn', 'Evelyn confirms it',
      'Look directly at Marcus and stop protecting yourself too.',
      'Face Marcus.',
      'I saw him take Nora’s coat off the chair. I kept quiet because I was hiding the letter I stole. I am done hiding it.'),

    line('murder-c48', 'murder-3', 'nora', 'Nora reconstructs the frame',
      'Put the whole sequence together without rushing.',
      'Put it together.',
      'You confronted Dr. Crowe after eight. The power failed. You killed him, closed the self-locking door behind you, put his key in my coat, then planted the ledger page in my bag after everyone started looking at me.'),

    line('murder-c49', 'murder-3', 'voss', 'Voss cracks',
      'Let anger break through the polished voice for the first time.',
      'Lose your calm.',
      'He was going to destroy everything over a few maps that nobody even knew were missing.'),

    line('murder-c50', 'murder-3', 'ortiz', 'Ortiz closes the case',
      'Step between Voss and everyone else.',
      'Step forward.',
      'Marcus Voss, do not move. That statement, the false alibi, the planted evidence, and the ledger give me more than enough to keep you here.'),

    line('murder-c51', 'murder-3', 'evelyn', 'Cheer for Nora',
      'Turn from Voss to Nora and let relief finally reach your face.',
      'Face Nora.',
      'Nora, you were the only person in this room everyone had already decided to doubt, and you still solved his murder.'),

    line('murder-c52', 'murder-3', 'nora', 'Nora completes the arc',
      'Look at the evidence, then meet Ortiz’s eyes without asking permission.',
      'Meet her eyes.',
      'I kept waiting for somebody more important to tell me I was not imagining what I saw. I am done doing that.'),

    {
      id: 'murder-c53', sceneId: 'murder-3', type: 'ending', visibility: 'selected',
      characterIds: ['nora', 'ortiz', 'evelyn'], label: 'Restore the truth',
      text: 'Decide what happens next to the stolen maps, the donor records, and Dr. Crowe’s unfinished investigation. Give Nora the final choice about what evidence gets preserved first.',
      requireAck: true,
    },
  ],
  script: [],
};
